import { useState, useEffect, useRef } from "react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CustomSelect } from "../ui/custom-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../ui/select";
import Pagination from "../ui/pagination";
import { Check, X } from "lucide-react";
import { useAuth } from "../../lib/authContext";

function FastMovingMeds({ entities }: { entities: any }) {
  const [fastMovingMeds, setFastMovingMeds] = useState<any[]>([]);
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  const [pharmacyMed, setPharmacyMed] = useState<any>(null);
  const [selectedMed, setSelectedMed] = useState<any>(null);
  const [searchResults, setSearchResults] = useState<any>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  const getFastMovingMeds = () => {
    if (!user?.id) return;
    api.getFastMovingMeds(page, limit, selectedEntity, user.id).then(data => {
      setFastMovingMeds(data?.data || []);
      setTotal(data?.totalCount || 0);
    }).catch(err => {
      console.error("Failed to fetch fast moving meds:", err);
      setFastMovingMeds([]);
      setTotal(0);
    });
  };

  useEffect(() => {
    if (selectedEntity) {
      getFastMovingMeds();
    }
  }, [page, limit, selectedEntity, user?.id]);

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };

  useEffect(() => {
    if (searchTerm?.length > 2 && selectedEntity) {
      api.searchPharmacyProducts(searchTerm, selectedEntity).then(data => {
        setSearchResults(data?.data || []);
      }).catch(err => {
        console.error("Failed to search pharmacy products:", err);
        setSearchResults([]);
      });
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, selectedMed, selectedEntity, user?.id]);

  const getDropdownPosition = (element: HTMLElement | null) => {
    if (!element) return "bottom";

    const rect = element.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Find the pagination component
    const paginationElement = document.querySelector(
      ".flex.justify-between.items-center.mt-4",
    );

    // Use the table element instead of looking for a specific max-height class
    const tableContainer = document.querySelector(".border.rounded-md");

    if (paginationElement && tableContainer) {
      const paginationRect = paginationElement.getBoundingClientRect();
      const tableRect = tableContainer.getBoundingClientRect();

      // Calculate distance to pagination and table boundaries
      const distanceFromTableBottom = tableRect.bottom - rect.bottom;
      const distanceFromTableTop = rect.top - tableRect.top;
      const distanceToPagination = paginationRect.top - rect.bottom;

      // If close to pagination (within 150px) or near table bottom
      if (
        distanceToPagination < 150 ||
        distanceFromTableBottom < 200 ||
        spaceBelow < 200
      ) {
        // Before showing on top, make sure there's enough space
        if (distanceFromTableTop > 200 && spaceAbove > 200) {
          return "top";
        }
      }
    }

    return "bottom";
  };

  const mapMed = (med: any) => {
    api.mapMed(pharmacyMed.id, med.id, user?.id || "").then(data => {
      setPharmacyMed(null);
      setSelectedMed(null);
      setSearchTerm("");
      if (data?.success) {
        toast({
          title: data.message || "Pharmacy product mapped",
        });
        getFastMovingMeds();
      } else {
        toast({
          title: data?.message || "Failed to map the pharmacy product",
        });
      }
    }).catch(err => {
        console.error("Failed to map medicine:", err);
        toast({
            title: "Error mapping medicine",
            variant: "destructive"
        });
    });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSelectedMed(null);
        setSearchTerm("");
        setPharmacyMed(null);
      }
    }

    if (selectedMed) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedMed]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex w-1/4">
        <CustomSelect
          options={entities.filter((entity: any) => Number(entity.value) !== 0)}
          value={selectedEntity}
          onChange={setSelectedEntity}
        />
      </div>
      <div className="mt-4 border rounded-md dark:border-gray-700">
        {selectedEntity ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product ID</TableHead>
                <TableHead>Product Name</TableHead>
                <TableHead>Packing</TableHead>
                <TableHead>Manufacturer</TableHead>
                <TableHead>MRP</TableHead>
                <TableHead>Map to Pharmacy</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fastMovingMeds?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    Please select the pharmacy to view the fast moving meds
                  </TableCell>
                </TableRow>
              ) : (
                   fastMovingMeds.map((med, idx) => (

                  <TableRow key={`${med?.id || idx}-${idx}`}>
                    <TableCell>{med?.drugcode}</TableCell>
                    <TableCell>{med?.drugname}</TableCell>
                    <TableCell>
                      {med?.drugquantity} {med?.drugmeasure} {med?.packform}
                    </TableCell>
                    <TableCell>{med.manufacturer}</TableCell>
                    <TableCell>₹{med.mrp}</TableCell>
                    <TableCell>
                      {pharmacyMed?.id && selectedMed?.id === med.id ? (
                        pharmacyMed.product_name
                      ) : (
                        <div className="relative">
                          <Input
                            id={`med-input-${med.id}`}
                            type="text"
                            placeholder="Search Pharmacy Product"
                            className="focus:ring-0 focus:ring-offset-0"
                            onClick={() => {
                               setSelectedMed(med);
                               const searchStr = med?.drugname ? med.drugname.split(" ")[0] : "";
                               handleSearch(searchStr);
                             }}
                            onChange={e => {
                              setSelectedMed(med);
                              handleSearch(e.target.value);
                            }}
                             value={
                               searchTerm && selectedMed?.id === med?.id
                                 ? searchTerm
                                 : ""
                             }
                          />
                           {searchTerm && selectedMed?.id === med?.id && (
                             <div
                               ref={dropdownRef}
                               className={`absolute ${
                                 getDropdownPosition(
                                   document.getElementById(
                                     `med-input-${med?.id}`,
                                   ),
                                 ) === "top"
                                   ? "bottom-full mb-1"
                                   : "top-full mt-1"
                               } z-50 w-full bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden`}
                             >
                               <div className="max-h-[200px] overflow-y-auto">
                                 {searchResults?.length === 0 ? (
                                   <div className="px-4 py-2 text-sm text-gray-500">
                                     No results found.
                                   </div>
                                 ) : (
                                    searchResults.map((result: any, idx: number) => (

                                     <div

                                       key={`${result?.id || idx}-${med?.id || idx}-${idx}`}
                                      className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                      onClick={() => {
                                        setPharmacyMed(result);
                                      }}
                                      onKeyDown={e => {
                                        if (e.key === "Enter") {
                                          setPharmacyMed(result);
                                        }
                                      }}
                                    >
                                      {result.product_name} (
                                      {result.product_packing}) - ₹{result.mrp}
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {pharmacyMed?.id && selectedMed?.id === med.id && (
                        <>
                          <Button
                            variant="outline"
                            className="bg-green-500"
                            onClick={() => mapMed(med)}
                          >
                            <Check size={16} />
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={() => setPharmacyMed(null)}
                            className="ml-2"
                          >
                            <X
                              size={16}
                              onClick={() => {
                                setPharmacyMed(null);
                                setSelectedMed(null);
                                setSearchTerm("");
                              }}
                            />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        ) : (
          <div className="px-4 py-2 text-lg font-bold">
            Please select the pharmacy to view the fast moving meds
          </div>
        )}
      </div>
      <div className="flex justify-between items-center mt-4">
        <div className="w-1/6">
          <Select
            value={limit.toString()}
            onValueChange={value => setLimit(Number(value))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select limit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500 mt-2">items per page</span>
        </div>
        <Pagination
          page={page}
          totalPages={Math.ceil(total / limit)}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}

export default FastMovingMeds;
